import { useEffect, useRef, useState } from "react";
import { Sun, Moon, Palette, LogOut, User as UserIcon, Mail, Shield, Sparkles, Trash2, Upload as UploadIcon, Image as ImageIcon, X } from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";
import ProjectSharing from "../components/ProjectSharing";
import { useAuth } from "@contexts/AuthContext";
import { useTheme } from "@contexts/ThemeContext";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";
import { extractPalette, buildThemeFromClusters } from "@shared/utils/extractPalette";
import useHeroImage from "@features/dashboard/hooks/useHeroImage";
import HeroImagePicker from "@features/dashboard/components/HeroImagePicker";

function ThemeOption({ active, onClick, onDelete, icon, label, description, swatches, activeBg, activeFg }) {
  return (
    <div
      className="relative flex flex-col gap-2 px-4 py-4 rounded-lg text-left transition-colors cursor-pointer"
      onClick={onClick}
      style={
        active
          ? { background: activeBg, color: activeFg || "white" }
          : { background: "var(--bg-input)", border: "1px solid var(--border-input)", color: "var(--text-secondary)" }
      }
    >
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute top-2 right-2 p-1.5 rounded-md transition-colors"
          style={{
            color: active ? "rgba(255,255,255,0.85)" : "var(--text-muted)",
            background: active ? "rgba(0,0,0,0.15)" : "transparent",
          }}
          title="Delete theme"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="flex items-center gap-2 text-sm font-semibold pr-6">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-center gap-1.5">
        {swatches.map((c, i) => (
          <span
            key={i}
            className="w-5 h-5 rounded-full"
            style={{ background: c, border: active ? "1px solid rgba(255,255,255,0.3)" : "1px solid rgba(0,0,0,0.1)" }}
          />
        ))}
      </div>
      <p className={`text-xs ${active ? "opacity-90" : ""}`} style={active ? undefined : { color: "var(--text-muted)" }}>
        {description}
      </p>
    </div>
  );
}

// Built-in themes — listed once and grouped by light/dark
const BUILTIN_THEMES = [
  {
    id: "dark",
    label: "Dark",
    isLight: false,
    icon: "moon",
    description: "Deep navy, cool accents",
    swatches: ["#0f1117", "#3b82f6", "#10b981", "#f59e0b"],
    activeBg: "#4f46e5",
  },
  {
    id: "midcentury",
    label: "Mid-Century",
    isLight: false,
    icon: "palette",
    description: "Slate charcoal, warm accents",
    swatches: ["#2C343D", "#C48D3B", "#AD614E", "#94A87C"],
    activeBg: "#C48D3B",
  },
  {
    id: "highcontrast",
    label: "High Contrast",
    isLight: false,
    icon: "sun",
    description: "Pure black, vivid accents",
    swatches: ["#000000", "#60A5FA", "#34D399", "#FBBF24"],
    activeBg: "#FFFFFF",
    activeFg: "#000000",
  },
  {
    id: "light",
    label: "Light",
    isLight: true,
    icon: "sun",
    description: "Warm cream, mid-century",
    swatches: ["#F5F1E6", "#5C7848", "#B07518", "#A4502E"],
    activeBg: "#B07518",
  },
  {
    id: "highcontrast-light",
    label: "HC Light",
    isLight: true,
    icon: "sun",
    description: "Pure white, bold accents",
    swatches: ["#FFFFFF", "#1D4ED8", "#047857", "#B45309"],
    activeBg: "#000000",
  },
  {
    id: "earthen",
    label: "Earthen",
    isLight: true,
    icon: "sun",
    description: "SW 2026 Grounded palette",
    swatches: ["#F2E8D5", "#7A2E27", "#3E5562", "#5C6334"],
    activeBg: "#7A2E27",
  },
];

const ICONS = {
  moon: <Moon className="w-4 h-4" />,
  sun: <Sun className="w-4 h-4" />,
  palette: <Palette className="w-4 h-4" />,
  sparkles: <Sparkles className="w-4 h-4" />,
};

export default function Settings() {
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const [themePref, setThemePref, prefLoaded] = useFirestoreDoc("theme-preference", null, { global: true });
  const [customThemes, setCustomThemes] = useFirestoreDoc("custom-themes", [], { global: true });
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState("");
  const [draftPalette, setDraftPalette] = useState(null);
  const [draftName, setDraftName] = useState("");
  const paletteFileRef = useRef(null);
  const heroFileRef = useRef(null);
  const { heroImage, setHeroFromFile, setHeroFromUrl, removeHero } = useHeroImage();
  const [heroUploading, setHeroUploading] = useState(false);
  const [showHeroPicker, setShowHeroPicker] = useState(false);

  // Sync the persisted preference to the active theme on first load
  useEffect(() => {
    if (prefLoaded && themePref && themePref !== theme) {
      setTheme(themePref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefLoaded, themePref]);

  const chooseTheme = (next) => {
    setTheme(next);
    setThemePref(next);
  };

  const handleSignOut = async () => {
    if (window.confirm("Sign out?")) {
      await signOut();
    }
  };

  const handlePaletteImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    setExtractError("");
    try {
      const palette = await extractPalette(file);
      setDraftPalette(palette);
      setDraftName("");
    } catch (err) {
      console.error(err);
      setExtractError("Couldn't read that image: " + err.message);
    } finally {
      setExtracting(false);
      e.target.value = "";
    }
  };

  const saveDraftTheme = () => {
    if (!draftPalette) return;
    const name = draftName.trim() || `Custom ${customThemes.length + 1}`;
    const id = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}`;
    // Strip the embedded clusters before saving to keep Firestore docs lean
    const { clusters: _omit, ...themeData } = draftPalette;
    const newTheme = { ...themeData, id, name };
    setCustomThemes([...(customThemes || []), newTheme]);
    setDraftPalette(null);
    setDraftName("");
    // Auto-apply the freshly saved theme
    chooseTheme(`custom:${id}`);
  };

  const setDraftMode = (mode) => {
    if (!draftPalette || !draftPalette.clusters) return;
    const rebuilt = buildThemeFromClusters(draftPalette.clusters, mode);
    setDraftPalette(rebuilt);
  };

  const cancelDraft = () => {
    setDraftPalette(null);
    setDraftName("");
  };

  const deleteCustomTheme = (id) => {
    if (!window.confirm("Delete this saved theme?")) return;
    setCustomThemes((customThemes || []).filter((t) => t.id !== id));
    if (theme === `custom:${id}`) {
      chooseTheme("dark");
    }
  };

  // Group themes by light/dark for display
  const allThemes = [
    ...BUILTIN_THEMES,
    ...(customThemes || []).map((t) => ({
      id: `custom:${t.id}`,
      rawId: t.id,
      label: t.name,
      isLight: !!t.isLight,
      icon: "sparkles",
      description: "Custom from image",
      swatches: (t.sourceColors || []).slice(0, 6),
      activeBg: (t.accents && t.accents.blue) || "#4f46e5",
      isCustom: true,
    })),
  ];
  const darkThemes = allThemes.filter((t) => !t.isLight);
  const lightThemes = allThemes.filter((t) => t.isLight);

  if (!user) return null;

  const displayName = user.displayName || user.email?.split("@")[0] || "User";
  const initials = displayName
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <InfoBentoCard
        cornerGlow
        icon={<UserIcon className="w-4 h-4" />}
        accent="purple"
        title="Profile & Settings"
        subtitle="Manage your account and app preferences"
        className="mb-6"
      />

      {/* Profile Card */}
      <InfoBentoCard cornerGlow frostedGradient accent="blue" className="mb-6">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Profile</p>
        <div className="flex items-center gap-4">
          {user.photoURL ? (
            <img src={user.photoURL} alt={displayName} className="w-16 h-16 rounded-full" />
          ) : (
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold text-white"
              style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}
            >
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-gray-200 truncate">{displayName}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <Mail className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-sm text-gray-400 truncate">{user.email}</span>
            </div>
            {user.emailVerified && (
              <div className="flex items-center gap-1.5 mt-1">
                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-xs text-emerald-400">Email verified</span>
              </div>
            )}
          </div>
        </div>
      </InfoBentoCard>

      {/* Theme Card */}
      <InfoBentoCard cornerGlow frostedGradient accent="amber" className="mb-6">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Appearance</p>
        <p className="text-sm text-gray-500 mb-5">Choose your default theme. This preference syncs across your devices.</p>

        {/* Dark themes */}
        <div className="mb-5">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2.5 flex items-center gap-1.5">
            <Moon className="w-3.5 h-3.5" /> Dark themes
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {darkThemes.map((t) => (
              <ThemeOption
                key={t.id}
                active={theme === t.id}
                onClick={() => chooseTheme(t.id)}
                onDelete={t.isCustom ? () => deleteCustomTheme(t.rawId) : undefined}
                icon={ICONS[t.icon]}
                label={t.label}
                description={t.description}
                swatches={t.swatches}
                activeBg={t.activeBg}
              />
            ))}
          </div>
        </div>

        {/* Light themes */}
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2.5 flex items-center gap-1.5">
            <Sun className="w-3.5 h-3.5" /> Light themes
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {lightThemes.map((t) => (
              <ThemeOption
                key={t.id}
                active={theme === t.id}
                onClick={() => chooseTheme(t.id)}
                onDelete={t.isCustom ? () => deleteCustomTheme(t.rawId) : undefined}
                icon={ICONS[t.icon]}
                label={t.label}
                description={t.description}
                swatches={t.swatches}
                activeBg={t.activeBg}
              />
            ))}
          </div>
        </div>
      </InfoBentoCard>

      {/* Custom palette from image */}
      <InfoBentoCard cornerGlow frostedGradient accent="purple" className="mb-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Generate from image
            </p>
            <p className="text-sm text-gray-500 mt-1.5">
              Upload any image — we'll extract its 6 main colors and turn them into a saveable theme.
            </p>
          </div>
        </div>

        {draftPalette && (
          <div className="mb-4 p-4 rounded-lg" style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)" }}>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Preview — 6 main colors</p>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {(draftPalette.sourceColors || []).slice(0, 6).map((c, i) => (
                <span
                  key={i}
                  className="w-12 h-12 rounded-md"
                  style={{ background: c, border: "1px solid rgba(0,0,0,0.15)" }}
                  title={c}
                />
              ))}
            </div>
            {/* Light/Dark mode toggle for the custom theme */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Mode:</span>
              <button
                onClick={() => setDraftMode("light")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
                  draftPalette.isLight ? "bg-amber-500 text-white" : "text-gray-400"
                }`}
                style={
                  draftPalette.isLight
                    ? undefined
                    : { background: "var(--bg-input)", border: "1px solid var(--border-input)" }
                }
              >
                <Sun className="w-3.5 h-3.5" /> Light
              </button>
              <button
                onClick={() => setDraftMode("dark")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
                  !draftPalette.isLight ? "bg-indigo-600 text-white" : "text-gray-400"
                }`}
                style={
                  !draftPalette.isLight
                    ? undefined
                    : { background: "var(--bg-input)", border: "1px solid var(--border-input)" }
                }
              >
                <Moon className="w-3.5 h-3.5" /> Dark
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="Theme name (e.g. Mood Board 1)"
                className="flex-1 min-w-[180px] text-sm text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)" }}
                onKeyDown={(e) => e.key === "Enter" && saveDraftTheme()}
                autoFocus
              />
              <button
                onClick={saveDraftTheme}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Save Theme
              </button>
              <button
                onClick={cancelDraft}
                className="text-sm text-gray-500 hover:text-gray-300 px-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => paletteFileRef.current?.click()}
            disabled={extracting}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-50"
          >
            <UploadIcon className="w-4 h-4" />
            {extracting ? "Extracting…" : draftPalette ? "Try another image" : "Upload image"}
          </button>
          <input
            ref={paletteFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePaletteImage}
          />
          <p className="text-xs text-gray-600">
            Try a paint chip photo, mood board, or product shot.
          </p>
        </div>

        {extractError && (
          <p className="text-sm text-red-400 mt-3">{extractError}</p>
        )}
      </InfoBentoCard>

      {/* Dashboard Cover Photo */}
      <InfoBentoCard cornerGlow frostedGradient accent="cyan" className="mb-6">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5" /> Dashboard Cover Photo
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Shown as the hero image on your dashboard. You can also set it from any image in the Design Book.
        </p>
        {heroImage?.url && (
          <div className="relative mb-4 rounded-lg overflow-hidden" style={{ maxHeight: "180px" }}>
            <img src={heroImage.url} alt="Current cover" className="w-full object-cover" style={{ maxHeight: "180px" }} />
            <button
              onClick={removeHero}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-gray-200 hover:text-red-400 transition-colors"
              title="Remove cover photo"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowHeroPicker(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
          >
            <ImageIcon className="w-4 h-4" />
            Choose from library
          </button>
          <button
            onClick={() => heroFileRef.current?.click()}
            disabled={heroUploading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
            style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)", color: "var(--text-secondary)" }}
          >
            <UploadIcon className="w-4 h-4" />
            {heroUploading ? "Uploading…" : "Upload new"}
          </button>
          <input
            ref={heroFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setHeroUploading(true);
              try { await setHeroFromFile(file); } finally { setHeroUploading(false); e.target.value = ""; }
            }}
          />
        </div>
      </InfoBentoCard>

      {/* Project Sharing */}
      <ProjectSharing />

      {/* Account Actions */}
      <InfoBentoCard cornerGlow frostedGradient accent="rose">
        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Account</p>
        <button
          onClick={handleSignOut}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
        <p className="text-xs text-gray-600 mt-3">
          Sign out to switch accounts or sign in as another user.
        </p>
      </InfoBentoCard>

      {showHeroPicker && (
        <HeroImagePicker
          currentUrl={heroImage?.url}
          onSelect={setHeroFromUrl}
          onClose={() => setShowHeroPicker(false)}
        />
      )}
    </div>
  );
}
